import { useEffect, useState } from 'react'
import { RuuviTag, PrometheusAlert } from '../types'
import LoadingState from './LoadingState'
import { useTranslation } from 'react-i18next'
import ErrorState from './ErrorState'
import { Alert, Card, CardContent, CardHeader, CardProps } from '@mui/material'
import { AxiosRequestConfig } from 'axios'
import { getAlerts } from '../services/alertService'
import AlertDataGrid from './AlertDataGrid'

export interface RuuviTagAlertHistoryProps extends Omit<CardProps, 'children'> {
  ruuviTag: RuuviTag
}

export default function RuuviTagAlertHistoryCard({ ruuviTag }: RuuviTagAlertHistoryProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The alerts state.
   */
  const [alerts, setAlerts] = useState<PrometheusAlert[]>([])

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * Load the alerts.
   */
  const loadRuuviTagAlerts = async (id: string, config: AxiosRequestConfig = {}) => {
    try {
      setLoading(true)
      setAlerts(
        await getAlerts(
          {
            ruuvitag: id,
            source: 'dynamodb'
          },
          config
        )
      )
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the alerts when the RuuviTag's ID changes.
   */
  useEffect(() => {
    const controller = new AbortController()
    loadRuuviTagAlerts(ruuviTag.id, { signal: controller.signal })
    return () => controller.abort()
  }, [ruuviTag.id])

  return (
    <Card>
      <CardHeader title={t('alertHistory')} />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : alerts.length === 0 ? (
          <Alert severity="info">{t('noPastAlerts')}</Alert>
        ) : (
          <AlertDataGrid alerts={alerts} showUnique={false} />
        )}
      </CardContent>
    </Card>
  )
}
